import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import {
  AlertCircle,
  Check,
  Loader2,
  Maximize,
  Play,
  RotateCw,
  Share2,
  Volume2,
  VolumeX,
} from "lucide-react";
import { useWhepPlayer } from "@/hooks/useWhepPlayer";
import {
  isNativeKeyTarget,
  resolveVideoKeyAction,
} from "@/components/video/controls/keymap";
import { LiveStreamTimer } from "./LiveStreamTimer";

/** Controls re-hide after this many ms of inactivity while the stream is playing. */
const CONTROLS_HIDE_DELAY_MS = 3000;
/** How long the copy-link button shows its "copied" confirmation before reverting. */
const COPIED_CONFIRMATION_MS = 2000;

/** iOS Safari's non-standard fullscreen entry point for <video>; absent from lib.dom.d.ts. */
type VideoWithFullscreenFallback = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void;
};

/** Screen Orientation API's lock(); not yet in lib.dom.d.ts and not universally supported. */
type OrientationWithLock = ScreenOrientation & {
  lock?: (orientation: string) => Promise<void>;
};

/** Best-effort landscape lock after entering fullscreen; unsupported/rejected is ignored. */
function lockLandscape(): void {
  const orientation: OrientationWithLock | undefined = screen.orientation;
  orientation?.lock?.("landscape")?.catch(() => undefined);
}

export interface LiveStreamPlayerProps {
  src: string;
  title: string;
  /** ISO timestamp; forwarded to LiveStreamTimer. */
  startTime?: string | null;
  isRunning?: boolean;
  /** @default "inline" */
  variant?: "inline" | "immersive" | "fill";
  /** When set on the immersive variant, shows a copy-link control. */
  shareUrl?: string;
  /**
   * Fill variant only. Content for the always-visible bottom strap, which the
   * consumer owns (Team Live tiles put identity, metrics and REC there). The
   * player appends its own auto-hiding mute/fullscreen cluster after it, and
   * drops its own LIVE badge and timer so the strap is the single source of
   * that information.
   */
  strap?: ReactNode;
  /**
   * Spread onto the strap element. Team Live uses it to make the strap the
   * drag surface for tile reordering, and to carry a test id / inline hiding
   * styles on the strap host.
   */
  strapProps?: HTMLAttributes<HTMLDivElement> & { "data-testid"?: string };
  /**
   * When set, a click on the video surface reports the tapped point
   * normalized to the ENCODED frame ([0,1], top-left origin) — compensating
   * for object-contain letterboxing. Team Live tap-to-focus (trackcast #328).
   */
  onVideoTap?: (nx: number, ny: number) => void;
}

interface ControlButtonProps {
  label: string;
  onClick: () => void;
  /** 28px instead of 36px, for the Team Live tile strap. */
  compact?: boolean;
  children: ReactNode;
}

/** Small round icon button used by the auto-hiding control layer. */
function ControlButton({ label, onClick, compact, children }: ControlButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      // Pointer-down on a control must not start a strap drag (Team Live).
      onPointerDown={(event) => event.stopPropagation()}
      aria-label={label}
      title={label}
      className={`inline-flex items-center justify-center rounded-full border border-white/20 bg-black/50 text-white backdrop-blur-sm transition-colors hover:bg-black/70 ${
        compact ? "h-7 w-7" : "h-9 w-9"
      }`}
    >
      {children}
    </button>
  );
}

export function LiveStreamPlayer({
  src,
  title,
  startTime,
  isRunning,
  variant = "inline",
  shareUrl,
  strap,
  strapProps,
  onVideoTap,
}: LiveStreamPlayerProps) {
  const { t } = useTranslation("pages");
  const { videoRef, status, error, needsTapToPlay, play, retry } = useWhepPlayer(src);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isMuted, setIsMuted] = useState(true);
  const [justCopied, setJustCopied] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);

  const isImmersive = variant === "immersive";
  // A consumer-owned strap takes over identity/status duty for the fill variant.
  const hasStrap = variant === "fill" && !!strap;
  // The control layer (and its auto-hide timer) must stay pinned open whenever
  // the stream isn't actively playing, or while it needs an explicit tap.
  const forceControlsVisible = status !== "playing" || needsTapToPlay;
  const showControls = controlsVisible || forceControlsVisible;

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current !== null) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const revealControls = useCallback(() => {
    setControlsVisible(true);
    clearHideTimer();
    if (forceControlsVisible) return;
    hideTimerRef.current = setTimeout(() => setControlsVisible(false), CONTROLS_HIDE_DELAY_MS);
  }, [forceControlsVisible, clearHideTimer]);

  // Reveal on mount, and re-arm the countdown whenever the "must stay visible"
  // condition changes (e.g. connecting -> playing starts the 3s auto-hide clock).
  useEffect(() => {
    revealControls();
    return clearHideTimer;
  }, [revealControls, clearHideTimer]);

  useEffect(() => {
    return () => {
      if (copiedTimerRef.current !== null) clearTimeout(copiedTimerRef.current);
    };
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      const next = !prev;
      if (videoRef.current) videoRef.current.muted = next;
      return next;
    });
  }, [videoRef]);

  const handleTapToPlay = useCallback(() => {
    setIsMuted(false);
    play();
  }, [play]);

  const handlePlayPause = useCallback(() => {
    if (needsTapToPlay) {
      // The stream needs an explicit gesture; the keyboard press counts as
      // one, so route through the same unmute-and-play path as the overlay.
      handleTapToPlay();
      return;
    }
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      play();
    } else {
      video.pause();
    }
  }, [handleTapToPlay, needsTapToPlay, play, videoRef]);

  // Letterbox-aware mapping (trackcast #328): object-contain fits the video
  // inside the element; convert the click to encoded-frame fractions and
  // clamp. Until metadata arrives, assume 16:9 inside the element rect.
  const handleVideoTapClick = useCallback(
    (event: MouseEvent<HTMLVideoElement>) => {
      if (!onVideoTap) return;
      const rect = event.currentTarget.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      const videoWidth = event.currentTarget.videoWidth || 16;
      const videoHeight = event.currentTarget.videoHeight || 9;
      const scale = Math.min(rect.width / videoWidth, rect.height / videoHeight);
      const contentWidth = videoWidth * scale;
      const contentHeight = videoHeight * scale;
      const offsetX = (rect.width - contentWidth) / 2;
      const offsetY = (rect.height - contentHeight) / 2;
      const nx = Math.min(1, Math.max(0, (event.clientX - rect.left - offsetX) / contentWidth));
      const ny = Math.min(1, Math.max(0, (event.clientY - rect.top - offsetY) / contentHeight));
      onVideoTap(nx, ny);
    },
    [onVideoTap],
  );

  const handleFullscreen = useCallback(() => {
    const el = containerRef.current;
    const video = videoRef.current as VideoWithFullscreenFallback | null;

    if (el?.requestFullscreen) {
      // Some environments (and this feature's tests) stub requestFullscreen
      // without a real Promise return value, so don't trust the return type.
      Promise.resolve(el.requestFullscreen())
        .then(lockLandscape)
        .catch(() => undefined);
    } else if (video?.webkitEnterFullscreen) {
      video.webkitEnterFullscreen();
    }
  }, [videoRef]);

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      // A handler closer to the target (e.g. the Team Live drag grip's
      // keyboard sensor) already claimed this keystroke; do not reinterpret
      // it as transport.
      if (event.defaultPrevented) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (isNativeKeyTarget(target)) {
        return;
      }
      const action = resolveVideoKeyAction(event);
      if (action === null) return;
      // Space on a focused button or link means "activate it" (mute,
      // fullscreen, retry); the transport toggle must not steal it.
      if (
        action === "play-pause" &&
        target &&
        (target.tagName === "BUTTON" || target.tagName === "A")
      ) {
        return;
      }
      // A live stream has no timeline; leave seek keys untouched so page
      // scrolling still works while the player is focused.
      if (
        action === "seek-back" ||
        action === "seek-forward" ||
        action === "seek-back-large" ||
        action === "seek-forward-large"
      ) {
        return;
      }
      // Key auto-repeat would flicker play/pause; one press, one toggle.
      if (event.repeat && action === "play-pause") {
        return;
      }
      event.preventDefault();
      switch (action) {
        case "play-pause":
          handlePlayPause();
          break;
        case "toggle-mute":
          toggleMute();
          break;
        case "toggle-fullscreen":
          handleFullscreen();
          break;
      }
      revealControls();
    },
    [handleFullscreen, handlePlayPause, revealControls, toggleMute],
  );

  const handleCopyLink = useCallback(() => {
    if (!shareUrl) return;
    // Optimistic: clipboard writes settle asynchronously, but the confirmation
    // doesn't need to wait on it, which also keeps this state update inside the
    // same synchronous click handler as the clipboard call itself.
    setJustCopied(true);
    if (copiedTimerRef.current !== null) clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = setTimeout(() => setJustCopied(false), COPIED_CONFIRMATION_MS);
    void navigator.clipboard.writeText(shareUrl).catch(() => undefined);
  }, [shareUrl]);

  // Inline stays pinned to a 16:9 card; immersive fills whatever container a
  // consumer (e.g. a full-bleed watch page) gives it; fill is the same sizing
  // but keeps the inline control layout for grid tiles (Team Live). The
  // <video> itself always uses object-contain so it letterboxes rather than
  // distorting. The wrapper is focusable for keyboard transport, so it swaps
  // the browser's default focus outline for the subtle ring WatchPlayer uses.
  const focusVisibleClassName =
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#60a5fa]";
  const wrapperClassName = `${
    variant === "immersive"
      ? "relative h-full w-full flex-1 overflow-hidden bg-black"
      : variant === "fill"
        ? "relative h-full w-full overflow-hidden bg-black"
        : "relative mb-4 w-full aspect-video overflow-hidden rounded-lg border border-secondary-200 bg-secondary-950"
  } ${focusVisibleClassName}`;

  const overlayVisibilityClassName = showControls
    ? "opacity-100 pointer-events-auto"
    : "opacity-0 pointer-events-none";

  const unmuteLabel = t(
    isMuted ? "sessions.detail.liveStream.unmute" : "sessions.detail.liveStream.mute",
    { ns: "pages" },
  );
  const copyLinkLabel = t(
    justCopied ? "sessions.detail.liveStream.linkCopied" : "sessions.detail.liveStream.copyLink",
    { ns: "pages" },
  );

  return (
    <div
      ref={containerRef}
      className={wrapperClassName}
      onPointerDown={revealControls}
      onMouseMove={revealControls}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      data-testid="live-stream-container"
    >
      <video
        ref={videoRef}
        title={title}
        data-testid="live-stream-viewer"
        onClick={onVideoTap ? handleVideoTapClick : undefined}
        className={`h-full w-full bg-black object-contain${onVideoTap ? " cursor-crosshair" : ""}`}
        autoPlay
        playsInline
        muted={isMuted}
      />

      {needsTapToPlay ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/45 px-4 text-center">
          <div className="pointer-events-auto flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={handleTapToPlay}
              aria-label={t("sessions.detail.liveStream.tapToPlay", { ns: "pages" })}
              className="flex h-16 w-16 items-center justify-center rounded-full bg-white text-secondary-900 shadow-lg transition-transform hover:scale-105"
            >
              <Play className="ml-1 h-7 w-7" fill="currentColor" />
            </button>
            <p aria-hidden="true" className="text-sm text-white/90">
              {t("sessions.detail.liveStream.tapToPlay", { ns: "pages" })}
            </p>
          </div>
        </div>
      ) : status === "connecting" ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/45 px-4 text-center">
          <div className="flex items-center gap-2 rounded-full bg-black/60 px-3 py-2 text-sm text-white">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t("sessions.detail.liveStream.connecting", { ns: "pages" })}
          </div>
        </div>
      ) : status === "reconnecting" ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/30 px-4 text-center">
          <div className="flex items-center gap-2 rounded-full bg-black/60 px-3 py-2 text-sm text-white">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t("sessions.detail.liveStream.reconnecting", { ns: "pages" })}
          </div>
        </div>
      ) : status === "error" ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/45 px-4 text-center">
          <div className="pointer-events-auto max-w-sm rounded-lg bg-black/70 px-4 py-3 text-sm text-white">
            <div className="flex items-center justify-center gap-2 font-medium">
              <AlertCircle className="h-4 w-4 text-red-300" />
              {t("sessions.detail.liveStream.unavailableTitle", { ns: "pages" })}
            </div>
            {error && <p className="mt-1 text-white/80">{error}</p>}
            <button
              type="button"
              onClick={retry}
              className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-secondary-900 hover:bg-secondary-100"
            >
              <RotateCw className="h-3.5 w-3.5" />
              {t("sessions.detail.liveStream.retry", { ns: "pages" })}
            </button>
          </div>
        </div>
      ) : null}

      {status === "playing" && !hasStrap && (
        <div
          className={`absolute left-3 top-3 flex items-center gap-2 transition-opacity duration-300 ${overlayVisibilityClassName}`}
        >
          <span className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
            {t("sessions.detail.liveStream.liveBadge", { ns: "pages" })}
          </span>
          <LiveStreamTimer startTime={startTime} isRunning={isRunning ?? status === "playing"} />
        </div>
      )}

      {isImmersive && shareUrl && (
        <div
          className={`absolute right-3 top-3 transition-opacity duration-300 ${overlayVisibilityClassName}`}
        >
          <ControlButton label={copyLinkLabel} onClick={handleCopyLink}>
            {justCopied ? <Check className="h-4 w-4" /> : <Share2 className="h-4 w-4" />}
          </ControlButton>
        </div>
      )}

      <div
        {...(hasStrap ? strapProps : undefined)}
        className={
          hasStrap
            ? "absolute inset-x-0 bottom-0 flex items-center gap-2 bg-gradient-to-t from-black/75 via-black/45 to-transparent px-2 py-1.5"
            : `absolute inset-x-0 bottom-0 flex items-center gap-2 p-3 transition-opacity duration-300 ${overlayVisibilityClassName}`
        }
      >
        {hasStrap && strap}
        {isImmersive && (
          <p className="min-w-0 flex-1 truncate text-sm font-semibold text-white drop-shadow">
            {title}
          </p>
        )}
        <div
          className={
            hasStrap
              ? `ml-auto flex shrink-0 items-center gap-1.5 transition-opacity duration-300 ${overlayVisibilityClassName}`
              : "ml-auto flex items-center gap-2"
          }
        >
          <ControlButton label={unmuteLabel} onClick={toggleMute} compact={hasStrap}>
            {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </ControlButton>
          <ControlButton
            label={t("sessions.detail.liveStream.fullscreen", { ns: "pages" })}
            onClick={handleFullscreen}
            compact={hasStrap}
          >
            <Maximize className="h-4 w-4" />
          </ControlButton>
        </div>
      </div>
    </div>
  );
}
